import type { Id, IsoDateTime } from "../primitives";
import { isReturningStage, type ShipmentStage } from "./tracking";

/**
 * Order stage (#465): one status per order, from the moment it is placed to the
 * moment it is delivered or received back, and for a COD order how the
 * confirmation call went.
 *
 * Nothing here talks to Shopify or the courier. It is the vocabulary the server
 * writes and the screens read, and `deriveOrderStage`, the one function that
 * says what stage a row is in. The API builds the same answer in SQL (so lists
 * can filter and sort on it) and a test holds the two to one answer, as it
 * does for `paymentKindOf`.
 */

/** The order's headline stage, in the order of the journey (sorting uses this). */
/** Hours a Confirmed order may wait for a shipment before the dashboard flags it (#528). */
export const NOT_SHIPPED_HOURS = 24;

export const ORDER_STAGES = [
  "new",
  "confirmed",
  "shipment_created",
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "attempt_failed",
  "delivered",
  "returning",
  "lost",
  "rto_received",
  "cancelled",
] as const;

export type OrderStage = (typeof ORDER_STAGES)[number];

export const ORDER_STAGE_LABELS: Record<OrderStage, string> = {
  new: "New",
  confirmed: "Confirmed",
  shipment_created: "Shipment created",
  picked_up: "Picked up",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  attempt_failed: "Attempt failed",
  delivered: "Delivered",
  returning: "Returning",
  lost: "Lost",
  rto_received: "RTO received",
  cancelled: "Cancelled",
};

/** The colour family of each stage's badge. */
export type StageTone = "grey" | "blue" | "amber" | "green" | "red" | "dark";

export const ORDER_STAGE_TONES: Record<OrderStage, StageTone> = {
  new: "grey",
  confirmed: "blue",
  shipment_created: "blue",
  picked_up: "blue",
  in_transit: "blue",
  out_for_delivery: "blue",
  attempt_failed: "amber",
  delivered: "green",
  returning: "red",
  lost: "red",
  rto_received: "dark",
  cancelled: "grey",
};

/**
 * The flow the "?" button draws: the normal journey, what happens when a
 * delivery fails, and cancelled, which can happen at any time before shipping.
 */
export const STAGE_FLOW_MAIN: readonly OrderStage[] = [
  "new",
  "confirmed",
  "shipment_created",
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "delivered",
];
export const STAGE_FLOW_FAILED: readonly OrderStage[] = [
  "attempt_failed",
  "returning",
  "lost",
  "rto_received",
];

/** One sentence per stage, for the "?" popover. */
export const ORDER_STAGE_HINTS: Record<OrderStage, string> = {
  new: "COD order waiting for a confirmation call.",
  confirmed: "Confirmed by a Buddy or manual tag, or prepaid. Waiting to ship.",
  shipment_created: "A shipment exists. The courier has not picked it up.",
  picked_up: "The courier has the parcel.",
  in_transit: "On its way to the customer.",
  out_for_delivery: "Out with the delivery agent today.",
  attempt_failed:
    "The courier could not deliver. It may go out again or start returning.",
  delivered: "Final stage.",
  returning: "On its way back to the warehouse.",
  lost: "The courier lost the parcel. A person set this; tracking never does and cannot change it.",
  rto_received: "The warehouse has checked the parcel back in.",
  cancelled: "Final stage.",
};

/** How a COD confirmation call went, read from the Buddy and manual tags. */
export const CALL_OUTCOMES = [
  "confirmed_buddy",
  "confirmed_manual",
  "cancel_requested",
  "address_updated",
  "attempt_2",
  "attempt_1",
  "busy",
  "no_answer",
] as const;

export type CallOutcome = (typeof CALL_OUTCOMES)[number];

/**
 * Listed in the order one wins when an order carries several outcome tags: a
 * confirmation beats everything, then a cancel request, then the latest
 * attempt. The API's SQL ranks by this same list.
 */
export const CALL_OUTCOME_PRIORITY: readonly CallOutcome[] = CALL_OUTCOMES;

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  confirmed_buddy: "Confirmed by Buddy",
  confirmed_manual: "Confirmed manually",
  cancel_requested: "Cancel requested",
  address_updated: "Address updated",
  attempt_2: "Attempt 2",
  attempt_1: "Attempt 1",
  busy: "Busy",
  no_answer: "No answer",
};

export const CALL_OUTCOME_TONES: Record<CallOutcome, StageTone> = {
  confirmed_buddy: "green",
  confirmed_manual: "green",
  cancel_requested: "red",
  address_updated: "amber",
  attempt_2: "amber",
  attempt_1: "amber",
  busy: "amber",
  no_answer: "amber",
};

/** The two outcomes that count as the order being confirmed. */
export function isConfirmingOutcome(outcome: CallOutcome | null): boolean {
  return outcome === "confirmed_buddy" || outcome === "confirmed_manual";
}

/** The risk label some tools add to an order. */
export const ORDER_RISKS = ["high", "low"] as const;
export type OrderRisk = (typeof ORDER_RISKS)[number];
export const ORDER_RISK_LABELS: Record<OrderRisk, string> = {
  high: "High risk",
  low: "Low risk",
};

/** What a tag means to the stage. `ignore` is a deliberate "not used". */
export const RULE_MEANINGS = [
  ...CALL_OUTCOMES,
  "risk_high",
  "risk_low",
  "ignore",
] as const;
export type RuleMeaning = (typeof RULE_MEANINGS)[number];

export const RULE_MEANING_LABELS: Record<RuleMeaning, string> = {
  ...CALL_OUTCOME_LABELS,
  risk_high: "High risk",
  risk_low: "Low risk",
  ignore: "Ignore",
};

/** A row of the translation table: "this tag means that", matched ignoring case. */
export interface StageRule {
  id: Id;
  tag: string;
  meaning: RuleMeaning;
}

export interface StageRuleInput {
  tag: string;
  meaning: RuleMeaning;
}

/** A tag seen on recent orders that no rule covers, with how often. */
export interface UnruledTag {
  tag: string;
  orders: number;
}

export interface OrderStageSettings {
  /** A COD order unconfirmed for longer than this is flagged. */
  overdueHours: number;
}

export const DEFAULT_ORDER_STAGE_SETTINGS: OrderStageSettings = {
  overdueHours: 12,
};

/**
 * The stage tag the ERP keeps on an order in Shopify (#465 part 2): one tag, a
 * fixed prefix so it never meets another tag. The broader names are on purpose:
 * Shipment created and Picked up read `ERP: Confirmed`, so the tag changes less
 * often than the stage does. Lost has no tag, and an order in it keeps whichever
 * tag it had.
 */
export const STAGE_TAG_PREFIX = "ERP: ";

export const STAGE_TAGS: Record<OrderStage, string | null> = {
  new: "ERP: New",
  confirmed: "ERP: Confirmed",
  shipment_created: "ERP: Confirmed",
  picked_up: "ERP: Confirmed",
  in_transit: "ERP: In transit",
  out_for_delivery: "ERP: Out for delivery",
  attempt_failed: "ERP: Attempt failed",
  delivered: "ERP: Delivered",
  returning: "ERP: Returning",
  lost: null,
  rto_received: "ERP: RTO received",
  cancelled: "ERP: Cancelled",
};

/** Whether a tag is one the ERP owns (matched ignoring case, as rules are). */
export function isStageTag(tag: string): boolean {
  return tag.toLowerCase().startsWith(STAGE_TAG_PREFIX.toLowerCase());
}

/** Stages after which an order is no longer open: tagged only when it moves into one. */
export const FINISHED_STAGES: readonly OrderStage[] = [
  "delivered",
  "lost",
  "rto_received",
  "cancelled",
];

/** The switch for writing the stage tag to Shopify; off until someone turns it on. */
export interface StageTagSettings {
  enabled: boolean;
  /** When it was last turned on: orders already finished by then are not tagged. */
  enabledAt: string | null;
}

/** A stage tag write Shopify refused or could not take, shown with a Retry. */
export interface StageTagFailure {
  orderId: Id;
  orderNumber: string;
  error: string;
  at: string;
}

export const OVERDUE_HOURS_BOUNDS = { min: 1, max: 720 } as const;

/** The tag "Mark confirmed" adds in Shopify, and "Undo" removes. */
export const MANUAL_CONFIRM_TAG = "Manually Confirmed";

/** The rules the table opens with (the ones the decision on #465 names). */
export const DEFAULT_STAGE_RULES: readonly StageRuleInput[] = [
  { tag: "✅ BUDDY-CONFIRM", meaning: "confirmed_buddy" },
  { tag: MANUAL_CONFIRM_TAG, meaning: "confirmed_manual" },
  { tag: "📵 BUDDY-NO_ANSWER", meaning: "no_answer" },
  { tag: "📞 BUDDY-BUSY", meaning: "busy" },
  { tag: "🔄 BUDDY-ATTEMPTED-1", meaning: "attempt_1" },
  { tag: "🔄 BUDDY-ATTEMPTED-2", meaning: "attempt_2" },
  { tag: "📍 BUDDY-ADDRESS_UPDATED", meaning: "address_updated" },
  { tag: "❌ BUDDY-CANCEL", meaning: "cancel_requested" },
  { tag: "High Risk", meaning: "risk_high" },
  { tag: "Low Risk", meaning: "risk_low" },
];

/** The shipment stage an order shows, where a shipment has one. */
export function orderStageOfShipment(
  stage: ShipmentStage | null,
): OrderStage | null {
  if (stage === null) return null;
  if (isReturningStage(stage) || stage === "rto_delivered") return "returning";
  switch (stage) {
    case "created":
      return "shipment_created";
    case "picked_up":
      return "picked_up";
    case "in_transit":
      return "in_transit";
    case "out_for_delivery":
      return "out_for_delivery";
    case "attempt_failed":
      return "attempt_failed";
    case "delivered":
      return "delivered";
    // A shipment that was cancelled, lost or damaged says nothing about the
    // order's journey; the order falls back to New or Confirmed.
    default:
      return null;
  }
}

/**
 * The stages a person may set by hand (#591, #680): any stage of the journey,
 * forward or back, because webhooks do not always move an order and a person
 * sometimes has to undo one that did. RTO received is deliberately not here
 * (it moves stock, so it stays the receiving flow of #529), and Cancelled
 * belongs to Shopify.
 */
export const MANUAL_STAGES = [
  "new",
  "confirmed",
  "shipment_created",
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "attempt_failed",
  "delivered",
  "returning",
  "lost",
] as const;
export type ManualStage = (typeof MANUAL_STAGES)[number];

export function isManualStage(value: unknown): value is ManualStage {
  return (MANUAL_STAGES as readonly unknown[]).includes(value);
}

/** The longest reason an override takes: one line, not a note. */
export const STAGE_OVERRIDE_REASON_MAX = 200;

/**
 * A stage a person set by hand: which, who, when and why. It holds until
 * someone chooses Back to automatic; a tracking update never replaces it.
 */
export interface StageOverride {
  stage: ManualStage;
  /** Who set it; null if that account has since been deleted. */
  setByName: string | null;
  setAt: IsoDateTime;
  reason: string;
}

/**
 * What the Change stage dialog sends: a stage, or null for Back to automatic,
 * and the comment that says why. The comment is required either way (#680);
 * it is kept with the stage and added to the order's comments.
 */
export interface StageOverrideInput {
  stage: ManualStage | null;
  /** One line. */
  reason: string;
}

/** How many orders a Change stage request changed, and how many it skipped. */
export interface StageOverrideOutcome {
  changed: number;
  skipped: number;
}

/**
 * Whether a person may change this order's stage by hand: not once Shopify
 * cancelled it, and not once the warehouse received its return.
 */
export function canChangeStage(order: {
  cancelledAt: IsoDateTime | null;
  rtoReceived: boolean;
}): boolean {
  return order.cancelledAt === null && !order.rtoReceived;
}

export interface OrderStageInput {
  /** The normalised payment gateway key (`cod`, `razorpay`, ...), null for an unpaid draft. */
  paymentGatewayKey: string | null;
  tags: readonly string[];
  cancelledAt: IsoDateTime | null;
  /** The stage of the shipment that speaks for the order (#527). */
  shipmentStage: ShipmentStage | null;
  rtoReceived: boolean;
  placedAt: IsoDateTime;
  /** A stage set by hand (#591); wins over the shipment, never over cancelled or received. */
  override?: ManualStage | null;
}

export interface OrderStageResult {
  stage: OrderStage;
  /** Only for a COD order; null otherwise. */
  confirmation: CallOutcome | null;
  risk: OrderRisk | null;
  /** Whole hours since placement when a New COD order is past the limit, else null. */
  overdueHours: number | null;
}

/**
 * The one definition of an order's stage.
 *
 * Cancelled wins, then a received return, then a stage set by hand, then the
 * shipment's journey; before any shipment, a COD order is New until a confirming tag arrives and any other
 * order is Confirmed. The API's SQL (`apps/api/src/stage/sql.ts`) is this
 * function in another language and a test holds the two together.
 */
export function deriveOrderStage(
  input: OrderStageInput,
  rules: readonly Pick<StageRule, "tag" | "meaning">[],
  settings: OrderStageSettings,
  now: Date,
): OrderStageResult {
  const meanings = new Set<RuleMeaning>();
  const byTag = new Map(rules.map((r) => [r.tag.toLowerCase(), r.meaning]));
  for (const tag of input.tags) {
    const meaning = byTag.get(tag.toLowerCase());
    if (meaning) meanings.add(meaning);
  }
  const isCod = input.paymentGatewayKey === "cod";
  const outcome = CALL_OUTCOME_PRIORITY.find((o) => meanings.has(o)) ?? null;
  const confirmed = isConfirmingOutcome(outcome);
  const risk: OrderRisk | null = meanings.has("risk_high")
    ? "high"
    : meanings.has("risk_low")
      ? "low"
      : null;

  let stage: OrderStage;
  if (input.cancelledAt !== null) stage = "cancelled";
  else if (input.rtoReceived) stage = "rto_received";
  else if (input.override) stage = input.override;
  else {
    stage =
      orderStageOfShipment(input.shipmentStage) ??
      (!isCod || confirmed ? "confirmed" : "new");
  }

  let overdueHours: number | null = null;
  if (stage === "new") {
    const hours = Math.floor(
      (now.getTime() - new Date(input.placedAt).getTime()) / 3_600_000,
    );
    if (hours >= settings.overdueHours) overdueHours = hours;
  }
  return { stage, confirmation: isCod ? outcome : null, risk, overdueHours };
}

/** Whether "Mark confirmed" applies: a COD order that is New. */
export function canMarkConfirmed(stage: OrderStage, isCod: boolean): boolean {
  return isCod && stage === "new";
}

/** One row of an order's stage history. */
export interface OrderStageChange {
  stage: OrderStage;
  enteredAt: IsoDateTime;
  /**
   * The time is a best guess: the row was rebuilt from scans and tags for an
   * order that pre-dates the history (#682), not recorded as it happened.
   */
  approximate: boolean;
  /** Why a person put the order in this stage, when one did (#591, #680). */
  manualReason: string | null;
}
